import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, X, UploadCloud, FileText, ChevronRight, AlertTriangle, Hash } from 'lucide-react';
import { useIncarichiData } from '../hooks/useIncarichiData';
import { AgendaListView } from './AgendaListView';
import { IncaricoDetailView } from './IncaricoDetailView';
import TurnoOperativoDashboard from '../components/mieiIncarichi/TurnoOperativoDashboard';
import { Modal } from '../components/Modal';
import { useToast } from '../components/ToastContext';
import { localIsoDate } from '../utils/utils';

const CATEGORIES = ['FOTOCAMERA', 'VIDEOCAMERA', 'SMARTPHONE', 'SPY', 'ALTRO'];

const formatBytes = (bytes: number) => {
    if (!bytes) return '';
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + ['B', 'KB', 'MB', 'GB'][i];
};

export const MieiIncarichiView: React.FC = () => {
    const hook = useIncarichiData();
    const location = useLocation();
    const navigate = useNavigate();
    const { addToast } = useToast();

    const {
        loading, incarichi, turni,
        viewMode, setViewMode,
        activeIncaricoId, setActiveIncaricoId,
        activeTurnoId, setActiveTurnoId,
        activeIncarico, activeTurno,
        filteredIncarichi, filteredTurni,
        incarichiSenzaReport, turniSenzaReport,
        isStoricoActive, isFuturoActive, pendingEvidence,
        actionLoading, uploadingFiles, uploadQueue,
        fotoDevice, setFotoDevice, videoDevice, setVideoDevice,
        // Modale conferma upload
        pendingUploadItems, isPendingConfirmOpen, setIsPendingConfirmOpen, confirmAndStartUpload,
        // Modale progresso upload
        uploadProgress, isProgressModalOpen, setIsProgressModalOpen,
        uploadSuccessStep, setUploadSuccessStep,
        // Data mancante
        missingDateQueue, setMissingDateQueue,
        currentMissingDateIndex, setCurrentMissingDateIndex,
        manualFileDate, setManualFileDate,
        pollingInterval,
        loadData, getTurnoCategory, formatDateTime,
        getPos, getFreshUrl,
        handleStartWithPhoto, handleStopWithPhoto, handleAccettaIncarico, handleRifiutaIncarico,
        handleFilesMultiUpload, handleManualDateSubmit, setUploadQueue,
        handleCreateUrgenteFromBlocked,
    } = hook;

    // Deep-link dalla Dashboard: apre l'incarico con il report da modificare.
    // Lo state si consuma una volta e si toglie dalla history, così refresh e
    // indietro non lo riapplicano. Tab e data valgono solo per l'incarico target.
    const deepLinkConsumed = useRef(false);
    const deepLinkInitial = useRef<{ incaricoId?: number; tab?: string; date?: string } | null>(null);
    useEffect(() => {
        if (deepLinkConsumed.current) return;
        const state = (location.state as any) ?? {};
        const openIncaricoId = state.openIncaricoId;
        if (!openIncaricoId) return;
        if (incarichi.length === 0) return;
        const exists = incarichi.some(i => i.id === openIncaricoId);
        if (!exists) return;
        deepLinkConsumed.current = true;
        const dateStr = typeof state.openDate === 'string' ? state.openDate.slice(0, 10) : undefined;
        deepLinkInitial.current = { incaricoId: openIncaricoId, tab: state.openTab, date: dateStr };
        setActiveIncaricoId(openIncaricoId);
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state, location.pathname, incarichi, setActiveIncaricoId, navigate]);

    // Deep-link `openTurnoId` dalle card turno della dashboard collaboratore.
    // Stesso schema di openIncaricoId ma con una ref separata: i due link sono indipendenti.
    const deepLinkTurnoConsumed = useRef(false);
    useEffect(() => {
        if (deepLinkTurnoConsumed.current) return;
        const state = (location.state as any) ?? {};
        const openTurnoId = state.openTurnoId;
        if (!openTurnoId) return;
        if (turni.length === 0) return;
        const exists = turni.some((t: any) => t.id === openTurnoId);
        if (!exists) return;
        deepLinkTurnoConsumed.current = true;
        setActiveTurnoId(openTurnoId);
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state, location.pathname, turni, setActiveTurnoId, navigate]);

    // Tab/data del deep-link valgono solo per l'incarico target.
    const deepLinkActiveProps = activeIncaricoId === deepLinkInitial.current?.incaricoId
        ? deepLinkInitial.current
        : null;

    // Reminder "Incarichi senza report": all'ingresso nella pagina mostra gli
    // incarichi passati ancora senza relazione. Il click apre l'incarico sul tab
    // Report. Si mostra una volta per mount e non se si è arrivati da un deep-link.
    const [isReminderOpen, setIsReminderOpen] = useState(false);
    const reminderShownRef = useRef(false);
    // Mandati e turni conclusi senza report, dal più recente. Il tipo dice al click
    // quale dettaglio aprire.
    const reminderItems = useMemo<Array<{ kind: 'incarico' | 'turno'; data: any }>>(() => {
        const dateOf = (it: { kind: string; data: any }) => it.kind === 'incarico'
            ? (it.data.dataInizio ? new Date(it.data.dataInizio).getTime() : 0)
            : (it.data.dataTurno ? new Date(it.data.dataTurno).getTime() : 0);
        return [
            ...incarichiSenzaReport.map((data: any) => ({ kind: 'incarico' as const, data })),
            ...turniSenzaReport.map((data: any) => ({ kind: 'turno' as const, data })),
        ].sort((a, b) => dateOf(b) - dateOf(a));
    }, [incarichiSenzaReport, turniSenzaReport]);
    useEffect(() => {
        if (reminderShownRef.current) return;
        if (loading) return;
        // Aspetta almeno il primo loadData prima di aprire il modale.
        if (incarichi.length === 0 && turni.length === 0) return;
        // Deep-link in corso: salta il reminder.
        if (activeIncaricoId !== null) {
            reminderShownRef.current = true;
            return;
        }
        reminderShownRef.current = true;
        if (reminderItems.length > 0) {
            setIsReminderOpen(true);
        }
    }, [loading, incarichi.length, turni.length, activeIncaricoId, reminderItems.length]);

    const handleReminderItemClick = (item: { kind: 'incarico' | 'turno'; data: any }) => {
        setIsReminderOpen(false);
        if (item.kind === 'turno') {
            setActiveTurnoId(item.data.id);
            return;
        }
        const inc = item.data;
        const day = inc.dataInizio ? String(inc.dataInizio).slice(0, 10) : undefined;
        // Stesso meccanismo del deep-link da Dashboard: deepLinkInitial viene passata
        // a IncaricoDetailView come tab e data iniziali.
        deepLinkInitial.current = { incaricoId: inc.id, tab: 'spese', date: day };
        setActiveIncaricoId(inc.id);
    };

    // Titolo e data della riga per tipo: turno con servizio e orari, incarico con l'helper esistente.
    const reminderRowTitle = (item: { kind: string; data: any }) =>
        item.kind === 'incarico'
            ? reminderItemTitle(item.data)
            : (item.data?.servizio?.nome || 'Servizio Fiduciario');
    const reminderRowDate = (item: { kind: string; data: any }) => {
        if (item.kind === 'incarico') {
            const inc = item.data;
            return inc.dataInizio && inc.dataFine && inc.dataInizio.slice(0, 10) !== inc.dataFine.slice(0, 10)
                ? `${formatItalianDate(inc.dataInizio)} → ${formatItalianDate(inc.dataFine)}`
                : formatItalianDate(inc.dataInizio);
        }
        const t = item.data;
        const day = formatItalianDate(t?.dataTurno);
        return t?.oraInizio ? `${day}  ${t.oraInizio}${t.oraFine ? ` – ${t.oraFine}` : ''}` : day;
    };

    const formatItalianDate = (iso?: string) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const reminderItemTitle = (inc: any) => {
        const praticaRawName = (inc.pratica as any)?.name || '';
        const prefix = praticaRawName.includes(' - ') ? praticaRawName.split(' - ')[0] : praticaRawName;
        const soggetto = inc.soggettoNome || (() => {
            try {
                const raw = (inc.pratica as any)?.datiAggiuntiviJson;
                const dati = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
                if (dati.soggetti?.[0]?.nome) return dati.soggetti[0].nome;
                return dati.soggetto_nome || dati.soggetto_denominazione || '';
            } catch { return ''; }
        })();
        return soggetto ? `${prefix} - ${soggetto}` : prefix || `Incarico #${inc.id}`;
    };

    // Incarichi futuri da accettare (badge tab)
    const todayStr = localIsoDate();
    const pendingFutureCount = incarichi.filter(inc =>
        inc.stato === 'ASSEGNATO' &&
        inc.dataInizio &&
        inc.dataInizio.split('T')[0] > todayStr
    ).length;

    // Totale incarichi + turni ASSEGNATO, indipendente da OGGI/FUTURI/STORICO,
    // per il badge "Da accettare".
    const pendingDaAccettareCount =
        incarichi.filter(inc => inc.stato === 'ASSEGNATO').length +
        turni.filter((t: any) => t.statoAccettazione === 'ASSEGNATO' && !t.dataRevoca).length;

    // Titolo incarico nei modali (pratica + soggetto). Per i REVOCATO solo
    // "Mandato #<id>", come in IncaricoCard.getIncaricoTitle.
    const praticaName = (() => {
        if (!activeIncarico) return '—';
        if (activeIncarico.stato === 'REVOCATO') {
            const mandatoId = (activeIncarico.pratica as any)?.id;
            return mandatoId ? `Mandato #${mandatoId}` : `Incarico #${activeIncarico.id}`;
        }
        const praticaRawName = (activeIncarico.pratica as any)?.name || '';
        const prefix = praticaRawName.includes(' - ') ? praticaRawName.split(' - ')[0] : praticaRawName;
        const soggetto = activeIncarico.soggettoNome || (() => {
            try {
                const raw = (activeIncarico.pratica as any)?.datiAggiuntiviJson;
                const dati = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
                if (dati.soggetti?.[0]?.nome) return dati.soggetti[0].nome;
                return dati.soggetto_nome || dati.soggetto_denominazione || '';
            } catch { return ''; }
        })();
        return soggetto ? `${prefix} - ${soggetto}` : prefix || `Incarico #${activeIncarico.id}`;
    })();

    // Caricamento iniziale
    if (loading && incarichi.length === 0 && turni.length === 0) {
        return (
            <div className="flex h-screen items-center justify-center bg-slate-50">
                <Loader2 className="animate-spin text-slate-800" size={40} />
            </div>
        );
    }

    // Dettaglio turno fiduciario
    if (activeTurnoId && activeTurno) {
        return (
            <TurnoOperativoDashboard
                turno={activeTurno}
                isStorico={getTurnoCategory(activeTurno) === 'STORICO'}
                onBack={() => setActiveTurnoId(null)}
                onRefresh={() => loadData(true)}
            />
        );
    }

    // Dettaglio incarico investigativo
    if (activeIncaricoId && activeIncarico) {
        const handleSkipMissingDate = () => {
            setUploadQueue((q: any) => ({ ...q, total: q.total - 1 }));
            const isLast = currentMissingDateIndex === missingDateQueue.length - 1;
            if (isLast) { setMissingDateQueue([]); setCurrentMissingDateIndex(0); }
            else setCurrentMissingDateIndex((p: number) => p + 1);
            setManualFileDate('');
        };

        return (
            <>
                <IncaricoDetailView
                    // La key forza il remount al cambio incarico, così lo state interno
                    // (selectedDate, activeTab...) riparte pulito.
                    key={activeIncarico.id}
                    activeIncarico={activeIncarico}
                    initialActiveTab={deepLinkActiveProps?.tab as any}
                    initialSelectedDate={deepLinkActiveProps?.date}
                    isStoricoActive={isStoricoActive}
                    isFuturoActive={isFuturoActive}
                    pendingEvidence={pendingEvidence}
                    actionLoading={actionLoading}
                    uploadingFiles={uploadingFiles}
                    uploadQueue={uploadQueue}
                    fotoDevice={fotoDevice}
                    setFotoDevice={setFotoDevice}
                    videoDevice={videoDevice}
                    setVideoDevice={setVideoDevice}
                    missingDateQueue={missingDateQueue}
                    setMissingDateQueue={setMissingDateQueue}
                    currentMissingDateIndex={currentMissingDateIndex}
                    setCurrentMissingDateIndex={setCurrentMissingDateIndex}
                    manualFileDate={manualFileDate}
                    setManualFileDate={setManualFileDate}
                    pollingInterval={pollingInterval}
                    onBack={() => setActiveIncaricoId(null)}
                    onRefresh={loadData}
                    getPos={getPos}
                    getFreshUrl={getFreshUrl}
                    onStartWithPhoto={handleStartWithPhoto}
                    onStopWithPhoto={handleStopWithPhoto}
                    onFilesMultiUpload={handleFilesMultiUpload}
                    onManualDateSubmit={handleManualDateSubmit}
                    onSkipMissingDate={handleSkipMissingDate}
                    onAccetta={handleAccettaIncarico}
                    onRifiuta={handleRifiutaIncarico}
                    onCreateUrgente={handleCreateUrgenteFromBlocked}
                />

                {/* Modale conferma upload */}
                {isPendingConfirmOpen && (
                    <Modal title="Conferma Caricamento" onClose={() => setIsPendingConfirmOpen(false)}>
                        <div className="space-y-5">
                            {/* Riepilogo */}
                            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 text-center">
                                <p className="text-[11px] font-black text-blue-400 uppercase tracking-widest mb-1">
                                    Stai caricando
                                </p>
                                <p className="text-5xl font-black text-blue-700 leading-none">
                                    {pendingUploadItems.length}
                                </p>
                                <p className="text-[11px] font-black text-blue-400 uppercase tracking-widest mt-1">
                                    {pendingUploadItems.length === 1 ? 'foto / file' : 'foto / file'} per la pratica
                                </p>
                                <p className="font-black text-slate-900 text-base mt-2 uppercase">{praticaName}</p>
                            </div>

                            {/* Anteprima file */}
                            <div className="max-h-40 overflow-y-auto space-y-1.5 border border-slate-100 rounded-xl bg-slate-50 p-3">
                                {pendingUploadItems.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-slate-100 shadow-sm">
                                        <img
                                            src={item.preview}
                                            className="w-8 h-8 rounded object-cover shrink-0"
                                            alt=""
                                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                        <span className="text-[10px] font-bold text-slate-600 truncate flex-1">{item.file.name}</span>
                                        <span className="text-[9px] text-slate-400 shrink-0">{formatBytes(item.file.size)}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Azioni */}
                            <div className="flex gap-3 pt-1">
                                <button
                                    onClick={() => setIsPendingConfirmOpen(false)}
                                    className="flex-1 py-4 text-slate-400 font-black uppercase text-[11px] border border-slate-100 rounded-2xl hover:bg-slate-50 transition-colors"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={confirmAndStartUpload}
                                    className="flex-[2] py-4 bg-blue-600 text-white rounded-2xl font-black uppercase text-[11px] shadow-lg hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                                >
                                    <UploadCloud size={16} /> Continua
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}

                {/* Overlay progresso upload */}
                {isProgressModalOpen && (
                    <div className="fixed inset-0 z-[200] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-6">
                        <div className="bg-white rounded-[2.5rem] p-10 w-full max-w-sm shadow-2xl text-center">
                            {!uploadSuccessStep ? (
                                <>
                                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <UploadCloud className="text-blue-600" size={32} />
                                    </div>
                                    <h3 className="font-black text-slate-900 uppercase text-xl tracking-tight">
                                        Caricamento in corso…
                                    </h3>
                                    <p className="text-[11px] text-slate-400 font-bold uppercase mt-2">
                                        {pendingUploadItems.length} {pendingUploadItems.length === 1 ? 'file' : 'file'} · {praticaName}
                                    </p>

                                    {/* Progress bar */}
                                    <div className="mt-8 h-3 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                                        <div
                                            className="h-full bg-blue-600 rounded-full transition-all duration-500"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                    <p className="mt-3 text-[13px] font-black text-slate-500 uppercase">{uploadProgress}%</p>

                                    {/* File in progress */}
                                    {Object.keys(uploadingFiles).length > 0 && (
                                        <div className="mt-4 space-y-1">
                                            {Object.entries(uploadingFiles).map(([name, pct]) => (
                                                <div key={name} className="flex items-center gap-2">
                                                    <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                                    </div>
                                                    <span className="text-[9px] text-slate-400 w-6 text-right font-bold">{pct}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="animate-in zoom-in duration-300">
                                    <div className="w-24 h-24 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                                        <CheckCircle2 size={48} />
                                    </div>
                                    <h3 className="font-black text-slate-900 uppercase text-2xl tracking-tight">
                                        Caricati con successo!
                                    </h3>
                                    <p className="text-[11px] text-slate-400 font-bold uppercase mt-3">
                                        {pendingUploadItems.length} {pendingUploadItems.length === 1 ? 'file caricato' : 'file caricati'} nella pratica
                                        <span className="block text-blue-600 mt-0.5">{praticaName}</span>
                                    </p>
                                    <button
                                        onClick={() => {
                                            setIsProgressModalOpen(false);
                                            setUploadSuccessStep(false);
                                        }}
                                        className="mt-8 w-full py-5 bg-slate-900 hover:bg-slate-800 text-white rounded-[1.5rem] font-black uppercase tracking-widest transition-colors"
                                    >
                                        OK
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    }

    // Lista agenda principale
    return (
        <div className="min-h-screen bg-slate-50 pb-24 text-slate-800 font-sans selection:bg-blue-100">
            <AgendaListView
                viewMode={viewMode}
                setViewMode={setViewMode}
                filteredIncarichi={filteredIncarichi}
                filteredTurni={filteredTurni}
                allIncarichi={incarichi}
                onSelectIncarico={(id: any) => {
                    // Un incarico RIFIUTATO resta in lista come traccia ma il dettaglio non si apre.
                    const target = incarichi.find((i: any) => i.id === id);
                    if (target && target.stato === 'RIFIUTATO') {
                        addToast('Incarico rifiutato: i dettagli non sono più consultabili.', 'info');
                        return;
                    }
                    setActiveIncaricoId(id);
                }}
                onSelectTurno={(id: any) => setActiveTurnoId(id)}
                onRefresh={loadData}
                formatDateTime={formatDateTime}
                onUrgenzaCreated={() => { setViewMode('OGGI'); loadData(true); }}
                pendingFutureCount={pendingFutureCount}
                pendingDaAccettareCount={pendingDaAccettareCount}
            />

            {/* Modale reminder: mandati e turni conclusi senza report */}
            {isReminderOpen && reminderItems.length > 0 && (
                <Modal title="Report da compilare" onClose={() => setIsReminderOpen(false)}>
                    <div className="space-y-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                            <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
                            <div className="text-sm text-amber-900">
                                Hai <span className="font-bold">{reminderItems.length}</span>{' '}
                                {reminderItems.length === 1 ? 'attività conclusa' : 'attività concluse'} per cui non hai ancora inviato il report.
                                Tocca una riga per aprirla e compilarla.
                            </div>
                        </div>

                        <div className="max-h-[55vh] overflow-y-auto space-y-2 -mx-1 px-1">
                            {reminderItems.map(item => (
                                <button
                                    key={`${item.kind}-${item.data.id}`}
                                    onClick={() => handleReminderItemClick(item)}
                                    className="w-full text-left bg-white border border-slate-200 hover:border-amber-300 hover:bg-amber-50/40 rounded-xl p-3 flex items-center gap-3 transition-colors shadow-sm"
                                >
                                    <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                                        <FileText size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[9px] font-bold tracking-wider text-slate-400 uppercase">
                                            {item.kind === 'incarico' ? 'Mandato' : 'Turno'}
                                        </p>
                                        <p className="font-semibold text-slate-900 truncate">{reminderRowTitle(item)}</p>
                                        {item.kind === 'incarico' && String(item.data?.pratica?.riferimento ?? '').trim() && (
                                            <span className="mt-1 inline-flex items-center gap-1 max-w-full bg-violet-50 border border-violet-200 text-violet-700 px-2 py-0.5 rounded-md text-xs font-black">
                                                <Hash size={12} className="shrink-0"/><span className="truncate">RIF. {String(item.data.pratica.riferimento).trim()}</span>
                                            </span>
                                        )}
                                        <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                                            {reminderRowDate(item)}
                                        </p>
                                    </div>
                                    <ChevronRight className="text-slate-400 shrink-0" size={18} />
                                </button>
                            ))}
                        </div>

                        <div className="pt-1">
                            <button
                                onClick={() => setIsReminderOpen(false)}
                                className="w-full py-3 text-slate-500 font-semibold text-sm border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                            >
                                Chiudi
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default MieiIncarichiView;